/**
 * Platform / Auth-Kit identity for Knowledge Assistant.
 *
 * Primary path (ka.nationaldatacare.com): shared Auth-Kit cookie
 * (`ndc-authenticated` marker + HttpOnly `wos-session`). The SPA calls
 * Auth-Kit `GET /api/session-token` with credentials and stores the WorkOS
 * access token for API calls.
 *
 * Fallback: Hub opener postMessage (`ndc-platform-auth-v1`) if cookies are
 * not yet shared.
 *
 * Uses `X-NDC-Access-Token` (not Authorization Bearer) so Azure Easy Auth does
 * not reject a non-Entra token.
 */

export const PLATFORM_AUTH_READY_TYPE = "ndc-ka-ready-v1";
export const PLATFORM_AUTH_MESSAGE_TYPE = "ndc-platform-auth-v1";

/** Must match NDC-Auth-Kit CLIENT_AUTH_MARKER_NAME */
export const AUTH_KIT_MARKER_NAME = "ndc-authenticated";

const STORAGE_TOKEN_KEY = "ndc_platform_access_token";
const STORAGE_USER_KEY = "ndc_platform_user";

const DEFAULT_PLATFORM_ORIGINS = [
  "https://app-dev.nationaldatacare.com",
  "https://app.nationaldatacare.com",
  "http://localhost:3010",
  "http://127.0.0.1:3010",
];

const DEFAULT_AUTH_PROD = "https://auth.nationaldatacare.com";
const DEFAULT_AUTH_STAGING = "https://auth-staging.nationaldatacare.com";
const DEFAULT_AUTH_LOCAL = "http://localhost:3005";

export type PlatformUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
};

export type PlatformSession = {
  accessToken: string;
  user: PlatformUser | null;
};

function allowedPlatformOrigins(): Set<string> {
  const fromEnv = (import.meta.env.VITE_PLATFORM_ORIGINS as string | undefined)
    ?.split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return new Set(fromEnv?.length ? fromEnv : DEFAULT_PLATFORM_ORIGINS);
}

export function isPlatformOrigin(origin: string): boolean {
  if (allowedPlatformOrigins().has(origin)) return true;
  try {
    const host = new URL(origin).hostname;
    return (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".nationaldatacare.com")
    );
  } catch {
    return false;
  }
}

/** True when Auth-Kit's client-readable session marker is on this host. */
export function hasAuthKitMarker(): boolean {
  if (typeof document === "undefined") return false;
  return new RegExp(`(?:^|;\\s*)${AUTH_KIT_MARKER_NAME}=`).test(document.cookie);
}

function stripSlash(url: string): string {
  return url.replace(/\/$/, "");
}

/**
 * Auth-Kit hosts to try for `GET /api/session-token`.
 * ka.nationaldatacare.com does not encode staging vs prod, so we infer from
 * referrer/opener when possible and otherwise try prod then staging.
 */
export function authKitBaseCandidates(): string[] {
  const seen = new Set<string>();
  const add = (raw: string | undefined | null) => {
    if (!raw) return;
    const url = stripSlash(raw.trim());
    if (url && !seen.has(url)) seen.add(url);
  };

  add(import.meta.env.VITE_NDC_AUTH_BASE_URL as string | undefined);

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      add(DEFAULT_AUTH_LOCAL);
      return [...seen];
    }

    try {
      const ref = document.referrer || "";
      if (ref.includes("app-dev.nationaldatacare.com") || ref.includes("auth-staging")) {
        add(DEFAULT_AUTH_STAGING);
      } else if (ref.includes("app.nationaldatacare.com") || ref.includes("://auth.nationaldatacare.com")) {
        add(DEFAULT_AUTH_PROD);
      }
    } catch {
      /* ignore */
    }

    add(DEFAULT_AUTH_PROD);
    add(DEFAULT_AUTH_STAGING);
  }

  return [...seen];
}

function parseSessionTokenPayload(data: unknown): PlatformSession | null {
  if (!data || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (typeof rec.accessToken !== "string" || !rec.accessToken.trim()) return null;
  return {
    accessToken: rec.accessToken.trim(),
    user: normalizeUser(rec.user),
  };
}

/**
 * Exchange the shared Auth-Kit cookie for a WorkOS access token.
 * Returns null when there is no marker, CORS/network fails, or the session
 * is invalid — caller should fall through to Easy Auth.
 */
export async function fetchAuthKitSession(): Promise<PlatformSession | null> {
  const existing = readPlatformSession();
  if (existing?.accessToken) return existing;

  if (typeof window === "undefined") return null;
  if (!hasAuthKitMarker()) return null;

  for (const base of authKitBaseCandidates()) {
    try {
      const res = await fetch(`${base}/api/session-token`, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (!res.ok) continue;
      const session = parseSessionTokenPayload(await res.json());
      if (session) {
        storePlatformSession(session);
        return session;
      }
    } catch {
      /* try next Auth-Kit host */
    }
  }

  return null;
}

export function isPlatformLaunch(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("from") === "platform") return true;
  try {
    return Boolean(window.opener && !window.opener.closed);
  } catch {
    return false;
  }
}

export function readPlatformSession(): PlatformSession | null {
  try {
    const accessToken = sessionStorage.getItem(STORAGE_TOKEN_KEY);
    if (!accessToken) return null;
    const rawUser = sessionStorage.getItem(STORAGE_USER_KEY);
    let user: PlatformUser | null = null;
    if (rawUser) {
      const parsed = JSON.parse(rawUser) as PlatformUser;
      if (parsed && typeof parsed.id === "string" && typeof parsed.email === "string") {
        user = parsed;
      }
    }
    return { accessToken, user };
  } catch {
    return null;
  }
}

export function storePlatformSession(session: PlatformSession): void {
  sessionStorage.setItem(STORAGE_TOKEN_KEY, session.accessToken);
  if (session.user) {
    sessionStorage.setItem(STORAGE_USER_KEY, JSON.stringify(session.user));
  } else {
    sessionStorage.removeItem(STORAGE_USER_KEY);
  }
}

export function clearPlatformSession(): void {
  sessionStorage.removeItem(STORAGE_TOKEN_KEY);
  sessionStorage.removeItem(STORAGE_USER_KEY);
}

function normalizeUser(raw: unknown): PlatformUser | null {
  if (!raw || typeof raw !== "object") return null;
  const u = raw as Record<string, unknown>;
  if (typeof u.id !== "string" || typeof u.email !== "string") return null;
  return {
    id: u.id,
    email: u.email,
    firstName: typeof u.firstName === "string" ? u.firstName : null,
    lastName: typeof u.lastName === "string" ? u.lastName : null,
  };
}

/**
 * Wait for Platform opener to post the Auth-Kit access token.
 * Resolves null on timeout / missing opener.
 */
export function awaitPlatformAuthHandoff(timeoutMs = 8000): Promise<PlatformSession | null> {
  const existing = readPlatformSession();
  if (existing) return Promise.resolve(existing);

  if (typeof window === "undefined") return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (session: PlatformSession | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
      resolve(session);
    };

    const onMessage = (event: MessageEvent) => {
      if (!isPlatformOrigin(event.origin)) return;
      const data = event.data as { type?: string; accessToken?: string; user?: unknown };
      if (!data || data.type !== PLATFORM_AUTH_MESSAGE_TYPE) return;
      if (typeof data.accessToken !== "string" || !data.accessToken.trim()) return;
      const session: PlatformSession = {
        accessToken: data.accessToken.trim(),
        user: normalizeUser(data.user),
      };
      storePlatformSession(session);
      finish(session);
    };

    window.addEventListener("message", onMessage);

    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: PLATFORM_AUTH_READY_TYPE },
          // Target origin is restricted on the Platform side when responding.
          "*",
        );
      }
    } catch {
      // Cross-origin opener blocked — handoff will time out.
    }

    const timer = window.setTimeout(() => finish(readPlatformSession()), timeoutMs);
  });
}

function primaryAuthKitBase(): string {
  return authKitBaseCandidates()[0] || DEFAULT_AUTH_PROD;
}

export function isNationalDatacareHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "nationaldatacare.com" || host.endsWith(".nationaldatacare.com");
}

/** Auth-Kit or Easy Auth sign-in URL for this host. */
export function signInHref(): string {
  if (typeof window === "undefined") return "/.auth/login/aad";
  const returnTo = `${window.location.origin}/`;
  if (isNationalDatacareHost()) {
    return `${primaryAuthKitBase()}/login?return_to=${encodeURIComponent(returnTo)}`;
  }
  return `/.auth/login/aad?post_login_redirect_url=${encodeURIComponent(
    window.location.pathname + window.location.search,
  )}`;
}

/**
 * Clear local Platform token and leave via Auth-Kit or Easy Auth logout.
 * On ka.nationaldatacare.com this clears the shared wos-session cookie.
 */
export function signOut(): void {
  const useAuthKit =
    hasAuthKitMarker() ||
    Boolean(readPlatformSession()?.accessToken) ||
    isNationalDatacareHost();
  clearPlatformSession();
  if (typeof window === "undefined") return;
  const returnTo = `${window.location.origin}/`;
  if (useAuthKit) {
    window.location.href = `${primaryAuthKitBase()}/logout?return_to=${encodeURIComponent(returnTo)}`;
    return;
  }
  window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(returnTo)}`;
}

/** Headers for Chat-Bot API calls when a Platform WorkOS session is present. */
export function platformAuthHeaders(
  session?: PlatformSession | null,
): Record<string, string> {
  const active = session ?? readPlatformSession();
  if (!active?.accessToken) return {};
  const headers: Record<string, string> = {
    "X-NDC-Access-Token": active.accessToken,
  };
  if (active.user) {
    headers["X-NDC-Platform-User"] = JSON.stringify({
      id: active.user.id,
      email: active.user.email,
      firstName: active.user.firstName ?? null,
      lastName: active.user.lastName ?? null,
    });
  }
  return headers;
}
